import * as THREE from 'three'

export type KillFeedEntry = {
  eliminator: string
  eliminated: string
  weapon: string
  weaponKindLabel?: string
  headshot?: boolean
  distanceM?: number
  timestamp: number
  /** Left accent bar color — typically weapon rarity tint. */
  rarityAccent?: string
}

interface ConfettiParticle {
  x: number
  y: number
  vx: number
  vy: number
  rot: number
  rotV: number
  color: string
  size: number
  wobble: number
  wobbleSpeed: number
}

const CONFETTI_COLORS = ['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7']
const KILL_FEED_DURATION = 4.0
const DEATH_CAM_DEFAULT_DURATION = 3.0
const VICTORY_TIME_SCALE = 0.3

export class FpsVictoryRoyale {
  private overlay!: HTMLDivElement
  private killFeed!: HTMLDivElement
  private deathCamOverlay!: HTMLDivElement
  private confettiCanvas!: HTMLCanvasElement
  private confettiCtx: CanvasRenderingContext2D | null = null
  private active = false
  private confettiParticles: ConfettiParticle[] = []
  private confettiRunning = false

  private killFeedEntries: Array<{ entry: KillFeedEntry; el: HTMLDivElement; timer: number }> = []

  private deathCamActive = false
  private deathCamTimer = 0
  private deathCamDuration = DEATH_CAM_DEFAULT_DURATION
  private deathCamTarget = new THREE.Vector3()
  private deathCamAngle = 0
  private deathCamPromptShown = false
  private deathCamKillerName = ''

  private victorySlowMo = false
  private victoryOrbit = false
  private victoryOrbitAngle = 0
  private victoryOrbitCenter = new THREE.Vector3()

  constructor(private mount: HTMLElement, private camera: THREE.Camera) {
    this.buildDOM()
  }

  // ─── DOM setup ────────────────────────────────────────────────────────────

  private buildDOM(): void {
    // Inject CSS for animations
    const style = document.createElement('style')
    style.textContent = `
      /* Phase 34X-25: snappier kill-feed slide with overshoot settle */
      @keyframes vr-slide-in {
        0%   { transform: translateX(120%); opacity: 0; }
        70%  { transform: translateX(-6%); opacity: 1; }
        100% { transform: translateX(0);   opacity: 1; }
      }
      @keyframes vr-fade-out {
        from { opacity: 1; }
        to   { opacity: 0; }
      }
      @keyframes vr-victory-pulse {
        0%,100% { text-shadow: 0 0 60px rgba(255,170,0,0.8), 0 0 120px rgba(255,200,60,0.4); }
        50%      { text-shadow: 0 0 100px rgba(255,210,0,1),  0 0 200px rgba(255,200,60,0.7); }
      }
      @keyframes vr-scale-in {
        from { transform: scale(0.7); opacity: 0; }
        to   { transform: scale(1);   opacity: 1; }
      }
      @keyframes vr-stat-pop {
        from { transform: translateY(10px) scale(0.94); opacity: 0; }
        to   { transform: translateY(0) scale(1); opacity: 1; }
      }
      .vr-stat-card {
        animation: vr-stat-pop 0.42s cubic-bezier(0.22,1,0.36,1) forwards;
        opacity: 0;
      }
      .vr-stat-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
        width: min(88vw, 400px);
      }
      @media (min-width: 520px) {
        .vr-stat-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); }
      }
      .vr-kill-entry {
        animation: vr-slide-in 0.38s cubic-bezier(0.22,1,0.36,1) forwards;
      }
      .vr-kill-entry.fading {
        animation: vr-fade-out 0.5s ease forwards;
      }
    `
    document.head.appendChild(style)

    // Main victory/defeat overlay
    this.overlay = document.createElement('div')
    this.overlay.style.cssText = [
      'position:fixed;inset:0;z-index:9100;display:none;flex-direction:column;',
      'align-items:center;justify-content:center;',
      'background:radial-gradient(ellipse 80% 60% at 50% 40%,',
      'rgba(255,180,40,0.18) 0%,rgba(5,8,16,0.88) 100%);',
      'pointer-events:auto;',
    ].join('')
    this.mount.appendChild(this.overlay)

    // Confetti canvas (above overlay)
    this.confettiCanvas = document.createElement('canvas')
    this.confettiCanvas.style.cssText = [
      'position:fixed;inset:0;z-index:9150;pointer-events:none;display:none;',
    ].join('')
    this.mount.appendChild(this.confettiCanvas)
    this.confettiCtx = this.confettiCanvas.getContext('2d')

    // Kill feed panel (top-right, independent)
    this.killFeed = document.createElement('div')
    this.killFeed.className = 'fps-kill-feed'
    this.killFeed.style.cssText = [
      'position:fixed;top:calc(env(safe-area-inset-top,0px) + 120px);right:calc(env(safe-area-inset-right,0px) + 14px);z-index:9200;',
      'display:flex;flex-direction:column;align-items:flex-end;gap:5px;',
      'pointer-events:none;',
      'font-family:var(--fps-font,system-ui,sans-serif)',
    ].join('')
    this.mount.appendChild(this.killFeed)

    // Death cam overlay
    this.deathCamOverlay = document.createElement('div')
    this.deathCamOverlay.style.cssText = [
      'position:fixed;inset:0;z-index:9050;display:none;flex-direction:column;',
      'align-items:center;justify-content:flex-end;padding-bottom:80px;',
      'pointer-events:none;',
    ].join('')
    this.mount.appendChild(this.deathCamOverlay)
  }

  // ─── Victory Royale ────────────────────────────────────────────────────────

  showVictory(
    playerName: string,
    kills: number,
    placement: number,
    orbitCenter?: THREE.Vector3,
  ): void {
    this.active = true
    this.victorySlowMo = true
    this.victoryOrbit = true
    this.victoryOrbitAngle = 0
    if (orbitCenter) this.victoryOrbitCenter.copy(orbitCenter)
    else this.victoryOrbitCenter.set(0, 0, 0)
    this.overlay.style.display = 'flex'

    this.overlay.innerHTML = `
      <div style="animation:vr-scale-in 0.55s cubic-bezier(0.22,1,0.36,1) forwards;
                  border:3px solid rgba(255,200,60,0.65);border-radius:20px;
                  padding:48px 64px 40px;text-align:center;
                  background:rgba(10,8,0,0.55);backdrop-filter:blur(18px);
                  -webkit-backdrop-filter:blur(18px);
                  box-shadow:0 0 80px rgba(255,200,0,0.2),inset 0 0 40px rgba(255,180,0,0.06)">

        <div style="font:900 clamp(52px,11vw,88px)/1.05 'Trebuchet MS',system-ui;
                    background:linear-gradient(180deg,#ffe066,#ffaa00,#ff8800);
                    -webkit-background-clip:text;-webkit-text-fill-color:transparent;
                    background-clip:text;
                    animation:vr-victory-pulse 2s ease-in-out infinite;
                    letter-spacing:0.04em">VICTORY ROYALE</div>

        <div style="margin-top:20px;font:700 clamp(14px,2vw,18px) 'Trebuchet MS',system-ui;
                    color:#ffe8a0;letter-spacing:0.2em;opacity:0.9">
          ★ &nbsp; #${placement} PLACEMENT &nbsp;·&nbsp; ${kills} ELIMINATION${kills !== 1 ? 'S' : ''} &nbsp; ★
        </div>

        <div style="margin-top:10px;font:600 13px system-ui;color:#ffcc66;
                    letter-spacing:0.12em;opacity:0.7">${playerName.toUpperCase()}</div>

        <div class="vr-stat-grid" style="margin-top:28px">
          ${this._statCell(String(placement), 'PLACEMENT', '0.12s')}
          ${this._statCell(String(kills), 'ELIMS', '0.22s')}
          ${this._statCell('WIN', 'OUTCOME', '0.32s')}
          ${this._statCell('#1', 'CROWN', '0.42s')}
        </div>

        <button id="vr-play-again" type="button"
          style="margin-top:36px;cursor:pointer;border:none;border-radius:10px;
                 padding:16px 52px;font:900 17px system-ui;letter-spacing:0.12em;
                 color:#1a0f00;background:linear-gradient(180deg,#ffe566,#ffc800,#ff9a00);
                 box-shadow:0 4px 0 #c97800,0 8px 28px rgba(255,180,0,0.45);
                 transition:transform 0.1s">PLAY AGAIN</button>
      </div>
    `

    this.overlay.querySelector('#vr-play-again')?.addEventListener('click', () => {
      window.location.reload()
    })

    // Phase 31X-17: confetti density scales with elim count
    this.spawnConfetti(100 + Math.min(80, kills * 12), true)
  }

  // ─── Defeat / Stats card ───────────────────────────────────────────────────

  showDefeat(
    placement: number,
    kills: number,
    damage: number,
    matsfarmed: number,
    killerName: string,
  ): void {
    this.active = true
    this.overlay.style.cssText = this.overlay.style.cssText.replace(
      /background:[^;]+;/,
      'background:radial-gradient(ellipse 80% 60% at 50% 40%,rgba(255,60,40,0.12) 0%,rgba(5,8,16,0.92) 100%);',
    )
    this.overlay.style.display = 'flex'

    this.overlay.innerHTML = `
      <div style="animation:vr-scale-in 0.45s cubic-bezier(0.22,1,0.36,1) forwards;
                  border:2px solid rgba(255,90,90,0.45);border-radius:18px;
                  padding:40px 56px 36px;text-align:center;
                  background:rgba(8,5,5,0.6);backdrop-filter:blur(18px);
                  -webkit-backdrop-filter:blur(18px);
                  box-shadow:0 0 60px rgba(255,60,60,0.15)">

        <div style="font:900 clamp(38px,8vw,60px)/1.05 'Trebuchet MS',system-ui;
                    color:#ff5a5a;text-shadow:0 0 24px rgba(255,60,60,0.6);
                    letter-spacing:0.06em">ELIMINATED</div>

        <div style="margin-top:8px;font:800 clamp(16px,3vw,22px) system-ui;
                    color:#ffb8b8;letter-spacing:0.1em">#${placement} PLACEMENT</div>

        <div class="vr-stat-grid" style="margin-top:24px">
          ${this._statCell(String(placement), 'PLACEMENT', '0.1s')}
          ${this._statCell(String(kills), 'ELIMS', '0.2s')}
          ${this._statCell(String(damage), 'DAMAGE', '0.3s')}
          ${this._statCell(String(matsfarmed), 'MATS', '0.4s')}
        </div>

        <div style="margin-top:18px;font:600 13px system-ui;color:#cc8888;letter-spacing:0.08em">
          Eliminated by: <span style="color:#ffaaaa;font-weight:800">${killerName}</span>
        </div>

        <div style="display:flex;gap:14px;margin-top:32px;justify-content:center">
          <button id="vr-play-again-d" type="button"
            style="cursor:pointer;border:none;border-radius:10px;
                   padding:14px 36px;font:800 15px system-ui;letter-spacing:0.1em;
                   color:#1a0f00;background:linear-gradient(180deg,#ffe566,#ffc800,#ff9a00);
                   box-shadow:0 4px 0 #c97800">PLAY AGAIN</button>
          <button id="vr-lobby-d" type="button"
            style="cursor:pointer;border:2px solid rgba(255,255,255,0.18);border-radius:10px;
                   padding:14px 36px;font:800 15px system-ui;letter-spacing:0.1em;
                   color:#ccd6e0;background:rgba(255,255,255,0.07)">LOBBY</button>
        </div>
      </div>
    `

    this.overlay.querySelector('#vr-play-again-d')?.addEventListener('click', () => {
      window.location.reload()
    })
    this.overlay.querySelector('#vr-lobby-d')?.addEventListener('click', () => {
      window.location.reload()
    })
  }

  private _statCell(value: string, label: string, delay = '0s'): string {
    return `<div class="vr-stat-card" style="animation-delay:${delay};padding:14px 12px;border-radius:12px;
                        background:linear-gradient(180deg,rgba(255,255,255,0.09) 0%,rgba(255,255,255,0.04) 100%);
                        border:1px solid rgba(255,255,255,0.12);
                        box-shadow:inset 0 1px 0 rgba(255,255,255,0.08),0 4px 16px rgba(0,0,0,0.22)">
      <div style="font:800 clamp(18px,4vw,24px) system-ui;color:#fff;line-height:1.1">${value}</div>
      <div style="font:700 9px system-ui;color:#8899aa;margin-top:6px;letter-spacing:0.12em">${label}</div>
    </div>`
  }

  // ─── Kill Feed ─────────────────────────────────────────────────────────────

  addKillFeedEntry(entry: KillFeedEntry): void {
    const el = document.createElement('div')
    el.className = 'fps-kill-feed-card'

    const accent = entry.rarityAccent ?? '#3399ff'

    const headshotBadge = entry.headshot
      ? `<span style="margin-left:4px;font-size:10px;color:#ffd700;font-weight:900" title="Headshot">⊕</span>`
      : ''

    const weaponIcon = this._weaponIcon(entry.weapon)
    const kindBadge = entry.weaponKindLabel
      ? `<span style="margin:0 4px;font:800 9px system-ui;letter-spacing:0.12em;color:#9fdcff">${this._esc(entry.weaponKindLabel)}</span>`
      : ''
    const distanceBadge =
      entry.distanceM != null && entry.distanceM >= 0
        ? `<span style="margin-left:6px;font-size:10px;font-weight:700;color:rgba(138,180,212,0.92);letter-spacing:0.04em">${entry.distanceM}m</span>`
        : ''

    el.style.cssText = [
      'position:relative;overflow:hidden;',
      'padding:7px 12px 7px 14px;border-radius:var(--fps-radius-sm,8px);',
      'background:var(--fps-panel-bg,rgba(8,12,22,0.82));',
      'border:1px solid rgba(255,255,255,0.1);',
      'border-left:4px solid ' + accent + ';',
      'backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);',
      'font:600 12px var(--fps-font,system-ui,sans-serif);white-space:nowrap;',
      'box-shadow:var(--fps-shadow,0 4px 18px rgba(0,0,0,0.45)),inset 0 1px 0 rgba(255,255,255,0.06);',
      'animation:fps-elim-slide-in 0.26s ease-out;',
    ].join('')

    el.innerHTML =
      `<span style="color:var(--fps-text,#fff);font-weight:700">${this._esc(entry.eliminator)}</span>` +
      `<span style="color:#667788;margin:0 5px">${weaponIcon}</span>` +
      kindBadge +
      `<span style="color:#ff8a8a;font-weight:600">${this._esc(entry.eliminated)}</span>` +
      headshotBadge +
      distanceBadge

    this.killFeed.appendChild(el)
    this.killFeedEntries.push({ entry, el, timer: KILL_FEED_DURATION })

    // Trim to max 5 visible entries
    while (this.killFeedEntries.length > 5) {
      const oldest = this.killFeedEntries.shift()!
      oldest.el.remove()
    }
  }

  private _weaponIcon(weapon: string): string {
    const w = weapon.toLowerCase()
    if (w.includes('sniper') || w.includes('bolt')) return '🎯'
    if (w.includes('shotgun') || w.includes('pump')) return '💥'
    if (w.includes('pistol') || w.includes('hand')) return '🔫'
    if (w.includes('rocket') || w.includes('launcher')) return '🚀'
    if (w.includes('smg') || w.includes('sub')) return '⚡'
    if (w.includes('pickaxe') || w.includes('axe')) return '⛏'
    return '✱'
  }

  private _esc(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  }

  // ─── Death Cam ─────────────────────────────────────────────────────────────

  startDeathCam(killerPos: THREE.Vector3, duration = DEATH_CAM_DEFAULT_DURATION, killerName = ''): void {
    this.deathCamActive = true
    this.deathCamTimer = duration
    this.deathCamDuration = duration
    this.deathCamTarget.copy(killerPos)
    this.deathCamAngle = 0
    this.deathCamPromptShown = false
    this.deathCamKillerName = killerName

    this.deathCamOverlay.style.display = 'flex'
    this.deathCamOverlay.innerHTML = `
      <div style="text-align:center;animation:vr-scale-in 0.3s cubic-bezier(0.22,1,0.36,1) forwards">
        <div id="vr-death-cam-text"
          style="font:900 clamp(16px,3vw,22px) system-ui;letter-spacing:0.25em;
                 color:#ff5a5a;text-shadow:0 0 20px rgba(255,60,60,0.7)">
          ELIMINATED${killerName ? ` BY <span style="color:#ffaaaa">${this._esc(killerName.toUpperCase())}</span>` : ''}
        </div>
        <div id="vr-death-cam-continue" style="margin-top:12px;opacity:0;transition:opacity 0.5s;
          font:600 13px system-ui;color:#8899aa;letter-spacing:0.1em">
          Press SPACE to continue
        </div>
      </div>
    `
  }

  stopDeathCam(): void {
    this.deathCamActive = false
    this.deathCamOverlay.style.display = 'none'
  }

  getTimeScale(): number {
    return this.victorySlowMo ? VICTORY_TIME_SCALE : 1
  }

  isVictoryOrbitActive(): boolean {
    return this.victoryOrbit
  }

  // ─── Confetti ──────────────────────────────────────────────────────────────

  private isGoldSilverConfetti = false;

  private spawnConfetti(count: number, isGoldSilver = false): void {
    this.isGoldSilverConfetti = isGoldSilver;
    this.confettiCanvas.width = window.innerWidth;
    this.confettiCanvas.height = window.innerHeight;
    this.confettiCanvas.style.display = 'block';
    this.confettiParticles = [];
    const num = isGoldSilver ? count * 1.5 : count;
    for (let i = 0; i < num; i++) {
      this.confettiParticles.push(this.createConfettiParticle(isGoldSilver, true));
    }
    this.confettiRunning = true;
  }

  private createConfettiParticle(isGoldSilver: boolean, initialRandomY = false): ConfettiParticle {
    const goldSilverColors = ['#FFD700', '#FFDF00', '#F0E68C', '#E6E6FA', '#DCDCDC', '#C0C0C0', '#B0C4DE'];
    const colors = isGoldSilver ? goldSilverColors : CONFETTI_COLORS;
    const y = initialRandomY
      ? -20 - Math.random() * window.innerHeight * 0.9
      : -20 - Math.random() * 30;

    return {
      x: Math.random() * window.innerWidth,
      y: y,
      vx: (Math.random() - 0.5) * 3.5,
      vy: 2.0 + Math.random() * 4.5,
      rot: Math.random() * Math.PI * 2,
      rotV: (Math.random() - 0.5) * 0.25,
      color: colors[Math.floor(Math.random() * colors.length)]!,
      size: 5 + Math.random() * 9,
      wobble: Math.random() * Math.PI * 2,
      wobbleSpeed: 0.04 + Math.random() * 0.12,
    };
  }

  private tickConfetti(dt: number): void {
    if (!this.confettiRunning || !this.confettiCtx) return;
    const ctx = this.confettiCtx;
    const W = this.confettiCanvas.width;
    const H = this.confettiCanvas.height;
    ctx.clearRect(0, 0, W, H);

    if (this.isGoldSilverConfetti && this.confettiParticles.length < 200) {
      const spawnRate = Math.floor(dt * 60);
      for (let i = 0; i < Math.max(1, spawnRate); i++) {
        this.confettiParticles.push(this.createConfettiParticle(true, false));
      }
    }

    let activeCount = 0;
    for (const p of this.confettiParticles) {
      p.wobble += p.wobbleSpeed;
      p.vx += Math.sin(p.wobble) * 0.08;
      p.x += p.vx + Math.cos(p.wobble) * 1.2;
      p.y += p.vy;
      p.rot += p.rotV;

      if (p.y < H + 30) {
        activeCount++;
      } else if (this.isGoldSilverConfetti) {
        const resetP = this.createConfettiParticle(true, false);
        Object.assign(p, resetP);
        activeCount++;
      }

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      ctx.globalAlpha = p.y > H * 0.8 ? Math.max(0, 1 - (p.y - H * 0.8) / (H * 0.2)) : 1;
      ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      ctx.restore();
    }

    if (activeCount === 0 && !this.isGoldSilverConfetti) {
      this.confettiRunning = false;
      this.confettiCanvas.style.display = 'none';
    }
  }

  // ─── Per-frame update ──────────────────────────────────────────────────────

  update(dt: number): void {
    // Kill feed timers
    for (const item of this.killFeedEntries) {
      item.timer -= dt
      if (item.timer < 0.5 && !item.el.classList.contains('fading')) {
        item.el.classList.add('fading')
      }
      if (item.timer <= 0) {
        item.el.remove()
      }
    }
    this.killFeedEntries = this.killFeedEntries.filter((it) => it.timer > 0)

    // Confetti
    this.tickConfetti(dt)

    // Victory Royale slow-mo orbit camera
    if (this.victoryOrbit) {
      this.victoryOrbitAngle += dt * 0.55
      const radius = 7.5
      const height = 2.85
      const cx = this.victoryOrbitCenter.x + Math.cos(this.victoryOrbitAngle) * radius
      const cz = this.victoryOrbitCenter.z + Math.sin(this.victoryOrbitAngle) * radius
      const cy = this.victoryOrbitCenter.y + height
      this.camera.position.set(cx, cy, cz)
      this.camera.lookAt(
        this.victoryOrbitCenter.x,
        this.victoryOrbitCenter.y + 1.2,
        this.victoryOrbitCenter.z,
      )
    }

    // Death cam orbit
    if (this.deathCamActive) {
      this.deathCamTimer -= dt
      this.deathCamAngle += dt * 0.8

      const radius = 6
      const height = 3
      const cx = this.deathCamTarget.x + Math.cos(this.deathCamAngle) * radius
      const cz = this.deathCamTarget.z + Math.sin(this.deathCamAngle) * radius
      const cy = this.deathCamTarget.y + height

      this.camera.position.set(cx, cy, cz)
      this.camera.lookAt(
        this.deathCamTarget.x,
        this.deathCamTarget.y + 1,
        this.deathCamTarget.z,
      )

      // Show "press space" prompt in last half
      if (!this.deathCamPromptShown && this.deathCamTimer < this.deathCamDuration * 0.5) {
        this.deathCamPromptShown = true
        const prompt = this.deathCamOverlay.querySelector<HTMLElement>('#vr-death-cam-continue')
        if (prompt) prompt.style.opacity = '1'
      }

      if (this.deathCamTimer <= 0) {
        this.stopDeathCam()
      }
    }
  }

  // ─── Hide / dispose ────────────────────────────────────────────────────────

  hide(): void {
    this.active = false
    this.victorySlowMo = false
    this.victoryOrbit = false
    this.overlay.style.display = 'none'
    this.deathCamOverlay.style.display = 'none'
    this.confettiCanvas.style.display = 'none'
    this.confettiRunning = false
  }

  dispose(): void {
    this.hide()
    this.overlay.remove()
    this.killFeed.remove()
    this.deathCamOverlay.remove()
    this.confettiCanvas.remove()
  }
}
